import React, { useEffect, useState, useMemo } from 'react';
import MUIDataTable from 'mui-datatables';
import axios from 'axios';
import config from '../config/config';
import {
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Fab,
  Divider,
  Avatar,
  RadioGroup,
  Radio,
  FormLabel,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  ContactPhone as ContactPhoneIcon,
  Home as HomeIcon,
  LocalHospital as HealthIcon,
  DirectionsBus as TransportIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Set Axios to include credentials globally
axios.defaults.withCredentials = true;

const Students = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    surname: '',
    admission_number: '',
    parent_firstname: '',
    parent_lastname: '',
    parent_surname: '',
    gender: '',
    date_of_birth: '',
    class_admitted_to: '',
    contact: '',
    physical_address: '',
    health_record: '',
    transport_option: '',
    status: 'active',
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const navigate = useNavigate();

  const classGrades = [
    { value: 'PG', label: 'Playgroup (2-3 years)' },
    { value: 'PP1', label: 'Pre-primary 1 (4-5 years)' },
    { value: 'PP2', label: 'Pre-primary 2 (4-5 years)' },
    { value: 'G1', label: 'Grade 1 (6-8 years)' },
    { value: 'G2', label: 'Grade 2 (6-8 years)' },
    { value: 'G3', label: 'Grade 3 (6-8 years)' },
    { value: 'G4', label: 'Grade 4 (9 years)' },
    { value: 'G5', label: 'Grade 5 (9 years)' },
    { value: 'G6', label: 'Grade 6 (11 years)' },
  ];

  const transportOptions = [
    { value: 'YES 1 WAY', label: 'Yes - 1 Way' },
    { value: 'YES 2 WAY', label: 'Yes - 2 Way' },
    { value: 'NO', label: 'No' },
  ];

  // Utility function to get cookie
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    if (match) return decodeURIComponent(match[2]);
    return null;
  };

  const fetchStudents = async (page = 0, rowsPerPage = 10, searchText = '') => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.get(`${config.API_URL}/api/students`, {
        params: { page, per_page: rowsPerPage, search: searchText },
        headers: { Authorization: `Bearer ${token}` },
      });

      //console.log('Fetch Students Response:', response.data);
      setData(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching students:', err);
      showSnackbar(err.response?.data?.error || 'Error fetching students', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents(page, rowsPerPage, searchText);
  }, [page, rowsPerPage, searchText]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleMenuClick = (event, student) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedStudent(student);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.get(`${config.API_URL}/api/students/${selectedStudent.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      //console.log('View API Response:', response.data);
      const studentData = response.data.student || response.data;
      if (!studentData) {
        throw new Error('No student data in response');
      }
      setSelectedStudent(studentData);
      setOpenModal(true);
    } catch (err) {
      console.error('Error fetching student details:', err);
      showSnackbar(err.response?.data?.error || 'Error fetching student details', 'error');
    } finally {
      setLoading(false);
    }
    handleMenuClose();
  };

  const handleEdit = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      if (!selectedStudent?.id) {
        throw new Error('No student ID selected');
      }

      const response = await axios.get(`${config.API_URL}/api/students/${selectedStudent.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      //console.log('Edit API Response:', response.data);

      const studentData = response.data.student || response.data;
      if (!studentData) {
        throw new Error('No student data in response');
      }

      const transformedData = {
        firstname: studentData.firstname || '',
        lastname: studentData.lastname || '',
        surname: studentData.surname || '',
        admission_number: studentData.admission_number || '',
        parent_firstname: studentData.parent_firstname || '',
        parent_lastname: studentData.parent_lastname || '',
        parent_surname: studentData.parent_surname || '',
        gender: studentData.gender || '',
        date_of_birth: studentData.date_of_birth || '',
        class_admitted_to: studentData.class_admitted_to || '',
        contact: studentData.contact || '',
        physical_address: studentData.physical_address || '',
        health_record: studentData.health_record || '',
        transport_option: studentData.transport_option || '',
        status: studentData.status || 'active',
      };

      //console.log('Transformed Data for Form:', transformedData);
      setFormData(transformedData);
      setIsEditing(true);
      setOpenAddEditModal(true);
    } catch (err) {
      console.error('Error fetching student for edit:', err);
      showSnackbar(err.message || 'Error fetching student data', 'error');
    } finally {
      setLoading(false);
    }
    handleMenuClose();
  };

  const handleAdd = () => {
    setIsEditing(false);
    setFormData({
      firstname: '',
      lastname: '',
      surname: '',
      admission_number: '',
      parent_firstname: '',
      parent_lastname: '',
      parent_surname: '',
      gender: '',
      date_of_birth: '',
      class_admitted_to: '',
      contact: '',
      physical_address: '',
      health_record: '',
      transport_option: '',
      status: 'active',
    });
    setSelectedFiles([]);
    setFormErrors({});
    setOpenAddEditModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedStudent(null);
  };

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    setIsEditing(false);
    setFormData({
      firstname: '',
      lastname: '',
      surname: '',
      admission_number: '',
      parent_firstname: '',
      parent_lastname: '',
      parent_surname: '',
      gender: '',
      date_of_birth: '',
      class_admitted_to: '',
      contact: '',
      physical_address: '',
      health_record: '',
      transport_option: '',
      status: 'active',
    });
    setSelectedFiles([]);
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleFileRemove = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstname.trim()) errors.firstname = 'First name is required';
    if (!formData.lastname.trim()) errors.lastname = 'Last name is required';
    if (!formData.admission_number.trim()) errors.admission_number = 'Admission number is required';
    if (!formData.parent_firstname.trim()) errors.parent_firstname = 'Parent first name is required';
    if (!formData.parent_lastname.trim()) errors.parent_lastname = 'Parent last name is required';
    if (!formData.gender) errors.gender = 'Gender is required';
    if (!formData.date_of_birth) errors.date_of_birth = 'Date of birth is required';
    if (!formData.class_admitted_to) errors.class_admitted_to = 'Class grade is required';
    if (!formData.contact.trim()) errors.contact = 'Telephone contact is required';
    if (!formData.physical_address.trim()) errors.physical_address = 'Physical address is required';
    if (!formData.transport_option) errors.transport_option = 'Transport option is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    alert('Edit feature is temporary disabled');
    return;
    if (!validateForm()) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      showSnackbar('No authentication token found', 'error');
      return;
    }

    try {
      // 1. Fetch CSRF cookie
      //console.log('Fetching CSRF cookie...');
      const csrfResponse = await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, {
        withCredentials: true,
      });
      //console.log('CSRF Response:', csrfResponse);

      // 2. Get CSRF token
      const xsrfToken = getCookie('XSRF-TOKEN');
      //console.log('XSRF Token:', xsrfToken);
      if (!xsrfToken) {
        throw new Error('CSRF token not found');
      }

      // 3. Prepare form data
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        // Ensure non-empty values are sent
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      selectedFiles.forEach((file, index) => {
        formDataToSend.append(`files[${index}]`, file);
      });

      // Log FormData for debugging
      //console.log('FormData being sent:');
      for (let [key, value] of formDataToSend.entries()) {
        //console.log(`${key}: ${value instanceof File ? value.name : value}`);
      }

      const url = isEditing
        ? `${config.API_URL}/api/students/${selectedStudent?.id}`
        : `${config.API_URL}/api/students`;

      const method = isEditing ? 'put' : 'post';

      // 4. Send request with CSRF token
      const response = await axios({
        method,
        url,
        data: formDataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          Accept: 'application/json',
          'X-XSRF-TOKEN': xsrfToken,
        },
        withCredentials: true,
      });

      //console.log('Submit Response:', response.data);

      showSnackbar(`Student ${isEditing ? 'updated' : 'added'} successfully!`, 'success');
      handleCloseAddEditModal();
      fetchStudents(page, rowsPerPage, searchText);
    } catch (error) {
      console.error('Error saving student:', error);
      //console.log('Error Response:', error.response?.data);
      showSnackbar(
        error.response?.data?.error || error.response?.data?.message || error.message || 'Error saving student',
        'error'
      );
    }
  };

  const toggleStatus = async (student) => {
    const token = localStorage.getItem('token');

    try {
      //console.log('Fetching CSRF cookie for toggle status...');
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, {
        withCredentials: true,
      });

      const xsrfToken = getCookie('XSRF-TOKEN');
      //console.log('XSRF Token for toggle:', xsrfToken);
      if (!xsrfToken) {
        throw new Error('CSRF token not found');
      }

      const response = await axios.patch(
        `${config.API_URL}/api/students/${student.id}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
          },
          withCredentials: true,
        }
      );

      //console.log('Toggle Status Response:', response.data);
      showSnackbar(
        `Student ${student.status === 'active' ? 'deactivated' : 'activated'} successfully!`,
        'success'
      );
      fetchStudents(page, rowsPerPage, searchText);
    } catch (error) {
      console.error('Error updating student status:', error);
      showSnackbar(
        error.response?.data?.error || error.response?.data?.message || error.message || 'Error updating student status',
        'error'
      );
    }
  };

  const columns = useMemo(
    () => [
      { name: 'id', label: 'ID', options: { display: false } },
      { name: 'admission_number', label: 'Admission No' },
      { name: 'name', label: 'Full Name' },
      { name: 'contact', label: 'Contact' },
      {
        name: 'gender',
        label: 'Gender',
        options: {
          customBodyRender: (value) => (
            <Chip
              label={value === 'M' ? 'Male' : 'Female'}
              color={value === 'M' ? 'primary' : 'secondary'}
              size="small"
            />
          ),
        },
      },
      { name: 'class_admitted_to', label: 'Class Grade' },
      { name: 'date_of_admission', label: 'Created On' },
      {
        name: 'status',
        label: 'Status',
        options: {
          customBodyRender: (value) => (
            <Chip
              label={value === 'active' ? 'Active' : 'Inactive'}
              color={value === 'active' ? 'success' : 'error'}
              size="small"
            />
          ),
        },
      },
      {
        name: 'actions',
        label: 'Actions',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value, tableMeta) => {
            const student = data[tableMeta.rowIndex];
            return (
              <IconButton
                onClick={(e) => handleMenuClick(e, student)}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
            );
          },
        },
      },
    ],
    [data]
  );

  const options = useMemo(
    () => ({
      search: false,
      filter: true,
      download: true,
      print: true,
      pagination: true,
      serverSide: true,
      count: total,
      rowsPerPage: rowsPerPage,
      page: page,
      onTableChange: (action, tableState) => {
        switch (action) {
          case 'changePage':
            setPage(tableState.page);
            break;
          case 'changeRowsPerPage':
            setRowsPerPage(tableState.rowsPerPage);
            setPage(0);
            break;
          default:
            break;
        }
      },
      onRowClick: (rowData, rowMeta) => {
        const student = data[rowMeta.dataIndex];
        navigate(`/students/${student.id}/invoices`);
      },
      elevation: 4,
      responsive: 'standard',
    }),
    [total, page, rowsPerPage, data, navigate]
  );

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search students..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            sx={{ width: 300 }}
            variant="outlined"
            size="small"
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' },
            }}
          >
            Add Student
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <MUIDataTable
            title="Students"
            data={data}
            columns={columns}
            options={options}
          />
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 8,
          sx: { minWidth: 150 },
        }}
      >
        <MenuItem onClick={handleView}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => { toggleStatus(selectedStudent); handleMenuClose(); }}>
          <Switch
            checked={selectedStudent?.status === 'active'}
            size="small"
            sx={{ mr: 1 }}
          />
          {selectedStudent?.status === 'active' ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>

      {/* View Student Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          <PersonIcon sx={{ mr: 1 }} />
          Student Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedStudent && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardHeader title="Personal Information" />
                  <CardContent>
                    <Typography><strong>ID:</strong> {selectedStudent.id}</Typography>
                    <Typography><strong>Admission No:</strong> {selectedStudent.admission_number}</Typography>
                    <Typography><strong>Full Name:</strong> {selectedStudent.name}</Typography>
                    <Typography><strong>Gender:</strong> {selectedStudent.gender === 'M' ? 'Male' : 'Female'}</Typography>
                    <Typography><strong>Date of Birth:</strong> {selectedStudent.date_of_birth}</Typography>
                    <Typography><strong>Class Grade:</strong> {selectedStudent.class_admitted_to}</Typography>
                    <Typography><strong>Status:</strong>
                      <Chip
                        label={selectedStudent.status === 'active' ? 'Active' : 'Inactive'}
                        color={selectedStudent.status === 'active' ? 'success' : 'error'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardHeader title="Parent Information" />
                  <CardContent>
                    <Typography><strong>Parent Name:</strong> {selectedStudent.parent_firstname} {selectedStudent.parent_lastname} {selectedStudent.parent_surname}</Typography>
                    <Typography><strong>Contact:</strong> {selectedStudent.contact}</Typography>
                    <Typography><strong>Physical Address:</strong> {selectedStudent.physical_address}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardHeader title="Health & Transport" />
                  <CardContent>
                    <Typography><strong>Health Record:</strong> {selectedStudent.health_record}</Typography>
                    <Typography><strong>Transport Option:</strong> {selectedStudent.transport_option}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Student Modal */}
      <Dialog open={openAddEditModal} onClose={handleCloseAddEditModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          {isEditing ? <EditIcon sx={{ mr: 1 }} /> : <AddIcon sx={{ mr: 1 }} />}
          {isEditing ? 'Edit Student' : 'Add New Student'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      <PersonIcon sx={{ mr: 1 }} />
                      Personal Information
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          name="firstname"
                          label="First Name"
                          value={formData.firstname}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          error={!!formErrors.firstname}
                          helperText={formErrors.firstname}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          name="lastname"
                          label="Last Name"
                          value={formData.lastname}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          error={!!formErrors.lastname}
                          helperText={formErrors.lastname}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          name="surname"
                          label="Surname"
                          value={formData.surname}
                          onChange={handleInputChange}
                          fullWidth
                          error={!!formErrors.surname}
                          helperText={formErrors.surname}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="admission_number"
                          label="Admission Number"
                          value={formData.admission_number}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          error={!!formErrors.admission_number}
                          helperText={formErrors.admission_number}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required error={!!formErrors.gender}>
                          <FormLabel>Gender</FormLabel>
                          <RadioGroup
                            row
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                          >
                            <FormControlLabel value="M" control={<Radio />} label="Male" />
                            <FormControlLabel value="F" control={<Radio />} label="Female" />
                          </RadioGroup>
                          {formErrors.gender && (
                            <Typography color="error" variant="caption">
                              {formErrors.gender}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="date_of_birth"
                          label="Date of Birth"
                          type="date"
                          value={formData.date_of_birth}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          InputLabelProps={{ shrink: true }}
                          error={!!formErrors.date_of_birth}
                          helperText={formErrors.date_of_birth}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required error={!!formErrors.class_admitted_to}>
                          <InputLabel>Class Grade</InputLabel>
                          <Select
                            name="class_admitted_to"
                            value={formData.class_admitted_to}
                            onChange={handleInputChange}
                            label="Class Grade"
                          >
                            {classGrades.map((grade) => (
                              <MenuItem key={grade.value} value={grade.value}>
                                {grade.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {formErrors.class_admitted_to && (
                            <Typography color="error" variant="caption">
                              {formErrors.class_admitted_to}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Parent Information */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      <ContactPhoneIcon sx={{ mr: 1 }} />
                      Parent Information
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          name="parent_firstname"
                          label="Parent First Name"
                          value={formData.parent_firstname}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          error={!!formErrors.parent_firstname}
                          helperText={formErrors.parent_firstname}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          name="parent_lastname"
                          label="Parent Last Name"
                          value={formData.parent_lastname}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          error={!!formErrors.parent_lastname}
                          helperText={formErrors.parent_lastname}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          name="parent_surname"
                          label="Parent Surname"
                          value={formData.parent_surname}
                          onChange={handleInputChange}
                          fullWidth
                          error={!!formErrors.parent_surname}
                          helperText={formErrors.parent_surname}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="contact"
                          label="Telephone Contact"
                          value={formData.contact}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          error={!!formErrors.contact}
                          helperText={formErrors.contact}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="physical_address"
                          label="Physical Address"
                          value={formData.physical_address}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          multiline
                          rows={2}
                          error={!!formErrors.physical_address}
                          helperText={formErrors.physical_address}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Health & Transport */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      <HealthIcon sx={{ mr: 1 }} />
                      Health & Transport
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="health_record"
                          label="Health Record"
                          value={formData.health_record}
                          onChange={handleInputChange}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Enter N/A if no health conditions or describe any medical conditions"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required error={!!formErrors.transport_option}>
                          <InputLabel>Transport Option</InputLabel>
                          <Select
                            name="transport_option"
                            value={formData.transport_option}
                            onChange={handleInputChange}
                            label="Transport Option"
                          >
                            {transportOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {formErrors.transport_option && (
                            <Typography color="error" variant="caption">
                              {formErrors.transport_option}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* File Upload */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      <CloudUploadIcon sx={{ mr: 1 }} />
                      File Attachments
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ border: '2px dashed #ccc', p: 2, textAlign: 'center' }}>
                      <input
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        id="file-upload"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                      <label htmlFor="file-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          sx={{ mb: 2 }}
                        >
                          Select Files
                        </Button>
                      </label>
                      <Typography variant="body2" color="textSecondary">
                        Upload documents, images, or other relevant files
                      </Typography>

                      {selectedFiles.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Selected Files:
                          </Typography>
                          {selectedFiles.map((file, index) => (
                            <Chip
                              key={index}
                              label={file.name}
                              onDelete={() => handleFileRemove(index)}
                              deleteIcon={<DeleteIcon />}
                              variant="outlined"
                              sx={{ m: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Status */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      Settings
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.status === 'active'}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: e.target.checked ? 'active' : 'inactive',
                            }))
                          }
                          color="primary"
                        />
                      }
                      label={`Student Status: ${formData.status === 'active' ? 'Active' : 'Inactive'}`}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseAddEditModal}
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' },
            }}
          >
            {isEditing ? 'Update Student' : 'Add Student'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleAdd}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          backgroundColor: '#1976d2',
          '&:hover': { backgroundColor: '#1565c0' },
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Students;